export interface AuditCheck {
  id: string;
  label: string;
  description: string;
  status: 'ok' | 'warning' | 'error';
  detail: string;
}

export interface RevenueSplitItem {
  id: string;
  source: string;
  radioPercent: number;
  rfwPercent: number;
  icon: string;
  color: string;
  checks: string[];
}

export interface ValuePropositionItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  linkedPage: string;
  linkedLabel: string;
  stats: string;
}

export interface MonetizationAuditData {
  checks: AuditCheck[];
  revenueSplits: RevenueSplitItem[];
  valueProps: ValuePropositionItem[];
  auditDate: string;
  overallStatus: 'ok' | 'warning' | 'error';
  passedChecks: number;
  totalChecks: number;
}

export const monetizationAuditData: MonetizationAuditData = {
  auditDate: '2026-06-17',
  overallStatus: 'ok',
  passedChecks: 5,
  totalChecks: 5,

  checks: [
    {
      id: 'check-01',
      label: 'Publicidad Propia → 100% Radio',
      description: 'La publicidad vendida directamente por cada radio (espacios propios, menciones, banners en su página) va 100% al bolsillo de la radio. RFW no cobra comisión sobre publicidad propia.',
      status: 'ok',
      detail: 'Revenue íntegro para la radio. Sin intermediación de RFW. Verificado en Own Ads y Ad Spaces.',
    },
    {
      id: 'check-02',
      label: 'Red Publicitaria RFW → 60% Radio / 40% RFW',
      description: 'Cuando una radio participa en la Red Publicitaria RFW, recibe el 60% del revenue generado. RFW retiene el 40% por operación, targeting, venta y gestión de la red.',
      status: 'ok',
      detail: 'Split 60/40 confirmado en Revenue Share y Ad Network. Misma proporción para todas las radios activas en la red.',
    },
    {
      id: 'check-03',
      label: 'Radio puede comprar publicidad',
      description: 'Las radios pueden actuar como anunciantes comprando publicidad en otras radios o en la Red Publicitaria RFW. El módulo Radio Anunciante cubre este flujo.',
      status: 'ok',
      detail: 'Radio Anunciante activo. Las radios pueden crear campañas, seleccionar targets y pagar por publicidad en la plataforma.',
    },
    {
      id: 'check-04',
      label: 'Marketplace separado',
      description: 'El Marketplace (licitaciones entre anunciantes y radios) y el Mkt Comercial operan como módulos independientes. El Ad Marketplace gestiona espacios publicitarios standard, el Mkt Comercial maneja licitaciones tipo subasta.',
      status: 'ok',
      detail: 'Dos marketplaces diferenciados: Ad Marketplace (violeta, comisión RFW) y Mkt Comercial (coral, 0% comisión actual). Sin solapamiento.',
    },
    {
      id: 'check-05',
      label: 'Payouts separado de Billing',
      description: 'Billing = Radios pagan a RFW (planes, cargos). Payouts = RFW paga a Radios (revenue de Red Publicitaria). Flujos financieros opuestos, módulos independientes.',
      status: 'ok',
      detail: 'Billing en Facturación (emerald), Payouts en Pagos a Radios (amber). Separación clara con identidad visual distinta y lógica de negocio independiente.',
    },
  ],

  revenueSplits: [
    {
      id: 'split-01',
      source: 'Publicidad Propia',
      radioPercent: 100,
      rfwPercent: 0,
      icon: 'ri-radio-button-line',
      color: 'emerald',
      checks: [
        'Sin comisión RFW',
        'Gestión 100% de la radio',
        'Espacios propios, menciones, banners',
        'Revenue íntegro para la radio',
      ],
    },
    {
      id: 'split-02',
      source: 'Red Publicitaria RFW',
      radioPercent: 60,
      rfwPercent: 40,
      icon: 'ri-global-line',
      color: 'amber',
      checks: [
        'RFW opera la red (targeting, venta, gestión)',
        'Radio recibe 60% del revenue generado',
        'RFW retiene 40% por operación',
        'Aplica a todas las radios en la red',
      ],
    },
  ],

  valueProps: [
    {
      id: 'vp-01',
      icon: 'ri-user-voice-line',
      title: 'Conseguir Audiencia',
      description: 'RFW conecta cada radio con oyentes reales a través de la plataforma, el directorio de radios, rankings y recomendaciones personalizadas. La radio gana visibilidad orgánica.',
      linkedPage: '/sa/analytics',
      linkedLabel: 'Ver Analytics',
      stats: '+2.4M oyentes únicos mensuales en la plataforma',
    },
    {
      id: 'vp-02',
      icon: 'ri-heart-add-line',
      title: 'Conseguir Seguidores',
      description: 'El sistema de comunidad RFW permite a las radios construir su base de seguidores, enviar notificaciones y fidelizar oyentes con contenido exclusivo.',
      linkedPage: '/sa/community-center',
      linkedLabel: 'Ver Comunidad',
      stats: 'Radios premium promedian +850 seguidores/mes',
    },
    {
      id: 'vp-03',
      icon: 'ri-advertisement-line',
      title: 'Conseguir Anunciantes',
      description: 'A través de la Red Publicitaria RFW y el Marketplace, las radios acceden a anunciantes que buscan espacios publicitarios segmentados por ubicación, género y audiencia.',
      linkedPage: '/sa/ad-marketplace',
      linkedLabel: 'Ver Marketplace',
      stats: '486 anunciantes activos · +32% interanual',
    },
    {
      id: 'vp-04',
      icon: 'ri-hand-heart-line',
      title: 'Conseguir Patrocinadores',
      description: 'RFW facilita la conexión con patrocinadores para programas, eventos y contenidos especiales. La plataforma visibiliza las oportunidades de sponsorship.',
      linkedPage: '/sa/sponsors',
      linkedLabel: 'Ver Patrocinadores',
      stats: '1,203 patrocinadores activos · +18% interanual',
    },
    {
      id: 'vp-05',
      icon: 'ri-money-dollar-circle-line',
      title: 'Generar Ingresos',
      description: 'Sumando publicidad propia (100%), Red Publicitaria (60%), licencias, membresías y cargos adicionales, la plataforma genera un ecosistema económico sostenible para las radios.',
      linkedPage: '/sa/economy',
      linkedLabel: 'Ver Economía RFW',
      stats: '$3.85M en ingresos para radios YTD 2026',
    },
  ],
};